import asyncForever from 'async/forever';

export default {
  noop,
  setDebugLogger,
  log,
  errorLog,
  errorQuietLog,
  debugLog,
  argsToString,
  shallowEqual,
  deepEqual,
  deepClone,
  stringHash,
  urlCat,
  between,
  durationToString,
  oneShot,
  capitalize,
  unPromise,
  repeat,
  filterInPlace,
  preventDefault,
  stopPropagation,
  stopAll,
  isDebug,
  now,
  byteToString,
};

function noop() {}

type Logger = (...args: any) => void;
let g_logger: Logger | null = null;
function setDebugLogger(logger: Logger) {
  g_logger = logger;
}
function log(...args: any) {
  console.log(...args);
}
function errorLog(...args: any) {
  console.error(...args);
  g_logger && g_logger(...args);
}
function errorQuietLog(...args: any) {
  console.log(...args);
  g_logger && g_logger(...args);
}
function debugLog(...args: any) {
  console.log(...args);
  g_logger && g_logger(...args);
}
function argsToString(): string {
  if (!arguments || arguments.length === 0) {
    throw new Error('log must have arguments');
  }
  let ret = '';
  for (let i = 0; i < arguments.length; i++) {
    const arg = arguments[i];
    if (i > 0) {
      ret += ' ';
    }

    if (_isError(arg)) {
      ret += arg.stack;
    } else if (typeof arg === 'object') {
      try {
        ret += JSON.stringify(arg);
      } catch (e) {
        ret += arg;
      }
    } else {
      ret += arg;
    }
  }
  return ret;
}
function _isError(e) {
  return (
    e &&
    e.stack &&
    e.message &&
    typeof e.stack === 'string' &&
    typeof e.message === 'string'
  );
}

function shallowEqual(objA, objB) {
  if (objA === objB) {
    return true;
  }

  if (
    typeof objA !== 'object' ||
    objA === null ||
    typeof objB !== 'object' ||
    objB === null
  ) {
    return false;
  }

  var keysA = Object.keys(objA);
  var keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) {
    return false;
  }

  // Test for A's keys different from B.
  var bHasOwnProperty = Object.prototype.hasOwnProperty.bind(objB);
  for (var i = 0; i < keysA.length; i++) {
    if (!bHasOwnProperty(keysA[i]) || objA[keysA[i]] !== objB[keysA[i]]) {
      return false;
    }
  }

  return true;
}

function deepEqual(x, y) {
  if (x === y) {
    return true;
  } else if (x === null || y === null) {
    return false;
  } else {
    if (typeof x === 'object' && typeof y === 'object') {
      for (let p in x) {
        if (x.hasOwnProperty(p)) {
          if (!y.hasOwnProperty(p)) {
            return false;
          } else if (!deepEqual(x[p], y[p])) {
            return false;
          }
        }
      }

      for (let p in y) {
        if (y.hasOwnProperty(p) && !x.hasOwnProperty(p)) {
          return false;
        }
      }
    } else {
      return false;
    }
    return true;
  }
}

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function stringHash(s) {
  let hash = 99;
  const len = (s && s.length) || 0;
  for (let i = 0; i < len; i++) {
    const chr = s.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }
  return hash;
}

function urlCat() {
  let url = '';
  for (let i = 0; i < arguments.length; i++) {
    const part = arguments[0];
    if (url.length > 0 && url.charAt(url.length - 1) !== '/') {
      url += '/';
    }
    if (part.charAt(0) === '/') {
      url += part.slice(1);
    } else {
      url += part;
    }
  }
  return url;
}

function between(value, min, max) {
  return Math.max(Math.min(value, max), min);
}

function durationToString(ms) {
  let seconds = Math.floor(ms / 1000);

  let minutes = Math.floor(seconds / 60);
  seconds -= minutes * 60;
  let hours = Math.floor(minutes / 60);
  minutes -= hours * 60;

  return (
    (hours > 0 ? pad(hours) + ':' : '') + pad(minutes) + ':' + pad(seconds)
  );
}

function pad(i) {
  return i < 10 ? '0' + i : i;
}

const g_one_shot_map = {};
function oneShot(key) {
  const ret = !(key in g_one_shot_map);
  if (ret) {
    g_one_shot_map[key] = true;
  }
  return ret;
}

function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function unPromise(promise, done) {
  try {
    promise
      .then((...result) => {
        done(null, ...result);
      })
      .catch((err) => {
        done(err);
      });
  } catch (e) {
    done(e);
  }
}

function repeat(callback, times, done) {
  if (!done) {
    done = function() {};
  }

  let count = 0;
  const BACKOFF_MS = 1 * 1000;

  asyncForever(
    (done) => {
      count++;
      if (count > times) {
        done('timeout');
      } else {
        callback((err) => {
          if (err) {
            setTimeout(done, BACKOFF_MS);
          } else {
            done('success');
          }
        });
      }
    },
    (err) => {
      if (err === 'success') {
        err = null;
      }
      done(err);
    }
  );
}

function filterInPlace(array, callback) {
  for (let i = array.length - 1; i >= 0; i--) {
    if (!callback(array[i], i)) {
      array.splice(i, 1);
    }
  }
}

function stopPropagation(e) {
  e && e.stopPropagation && e.stopPropagation();
}
function preventDefault(e) {
  e && e.preventDefault && e.preventDefault();
}
function stopAll(e) {
  stopPropagation(e);
  preventDefault(e);
}

function isDebug() {
  const is_local = window.location.origin.indexOf(':30') !== -1;

  return is_local;
}
function now() {
  if (window.performance && window.performance.now) {
    return window.performance.now();
  } else {
    return Date.now();
  }
}

const KB = 1024;
const MB = 1024 * KB;
const GB = 1024 * MB;

export function byteToString(bytes) {
  let ret;
  if (bytes > GB) {
    ret = `${(bytes / GB).toFixed(3)}GB`;
  } else if (bytes > MB) {
    ret = `${(bytes / MB).toFixed(3)}MB`;
  } else if (bytes > KB) {
    ret = `${(bytes / KB).toFixed(3)}KB`;
  } else {
    ret = bytes + 'B';
  }
  return ret;
}
